import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Strategies.module.css';
import StrategyCard from '../../components/strategies/StrategyCard/StrategyCard';
import StrategyStats from '../../components/strategies/StrategyStats/StrategyStats';
import StrategyForm from '../../components/strategies/StrategyForm/StrategyForm';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import Spinner from '../../components/common/Spinner/Spinner';
import { useStrategies } from '../../hooks/useStrategies';
import { usePermission } from '../../hooks/usePermissions';
import { toast } from 'react-toastify';

const Strategies = () => {
    const {
        strategies, total, signalsToday, loading, error, isAdmin,
        fetchStrategies, createStrategy, updateStrategy,
        deleteStrategy, toggleStrategy, clearError,
    } = useStrategies();

    const { hasPermission } = usePermission();
    const canCreate = hasPermission("CREATE_STRATEGYENGINE");
    const canEdit = hasPermission("MODIFY_STRATEGYENGINE");
    const canDelete = hasPermission("DELETE_STRATEGYENGINE");

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // "My Strategies" view passes ?mine=true; "All Strategies" (admin default)
    // omits it. Non-admins only ever see their own regardless (server-enforced).
    const mine = searchParams.get('mine') === 'true';
    const listFilters = useMemo(() => (mine ? { mine: 'true' } : {}), [mine]);

    const [page, setPage] = useState(1);
    const limit = 12;
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [strategyToDelete, setStrategyToDelete] = useState(null);
    const [strategyToEdit, setStrategyToEdit] = useState(null);
    const [formData, setFormData] = useState({});
    const [initialLoaded, setInitialLoaded] = useState(false);

    // Stats calculation
    const stats = useMemo(() => ({
        total: total,
        active: strategies.filter(s => s.is_active).length,
        signalsToday,
    }), [strategies, total, signalsToday]);

    // Form progress. `symbols` is the multi-symbol array (length > 0
    // counts as filled). We still check the legacy `symbol` string as
    // a fallback so a parent who only mirrored the old field doesn't
    // make the bar regress.
    const formProgress = useMemo(() => {
        const hasSymbols = (Array.isArray(formData.symbols) && formData.symbols.length > 0)
            || (formData.symbol && String(formData.symbol).trim());
        const otherFields = [formData.name, formData.timeframe, formData.action];
        const filled = otherFields.filter(f => f && String(f).trim()).length + (hasSymbols ? 1 : 0);
        const conditionsFilled = formData.conditions?.length > 0 ? 1 : 0;
        return ((filled + conditionsFilled) / 5) * 100;
    }, [formData]);

    useEffect(() => {
        fetchStrategies(page, limit, listFilters)
            .then(() => setInitialLoaded(true))
            .catch(() => {});
    }, [page, limit, fetchStrategies, listFilters]);

    const handleCreate = async (data) => {
        try {
            await createStrategy(data);
            setShowCreateModal(false);
            setFormData({});
            toast.success('Strategy created successfully!');
            fetchStrategies(page, limit, listFilters);
        } catch (err) {
            toast.error(err.message || 'Failed to create strategy');
        }
    };

    const handleEdit = (strategy) => {
        setStrategyToEdit(strategy);
        // Mirror the strategy into the parent's formData so the
        // progress bar starts pre-filled. Multi-symbol: prefer the
        // `symbols` array from the backend; fall back to wrapping the
        // legacy `symbol` string for older responses.
        const symbols = Array.isArray(strategy.symbols) && strategy.symbols.length > 0
            ? strategy.symbols
            : (strategy.symbol ? [strategy.symbol] : []);
        // position_sizing may come back as an object (parsed JSONB)
        // or a stringified blob depending on response path — handle
        // both so the progress bar reflects the actual saved state.
        let positionSizing = strategy.position_sizing ?? null;
        if (typeof positionSizing === 'string') {
            try { positionSizing = JSON.parse(positionSizing); }
            catch { positionSizing = null; }
        }
        setFormData({
            name: strategy.name,
            symbols,
            timeframe: strategy.timeframe,
            action: strategy.action,
            conditions: strategy.conditions,
            position_sizing: positionSizing,
        });
        setShowEditModal(true);
    };

    const handleUpdate = async (data) => {
        if (!strategyToEdit) return;
        try {
            await updateStrategy(strategyToEdit.id, data);
            setShowEditModal(false);
            setStrategyToEdit(null);
            setFormData({});
            toast.success('Strategy updated successfully!');
            fetchStrategies(page, limit, listFilters);
        } catch (err) {
            toast.error(err.message || 'Failed to update strategy');
        }
    };

    const handleDeleteClick = (strategy) => {
        setStrategyToDelete(strategy);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!strategyToDelete) return;
        try {
            await deleteStrategy(strategyToDelete.id);
            setShowDeleteModal(false);
            setStrategyToDelete(null);
            toast.success('Strategy deleted');
            fetchStrategies(page, limit, listFilters);
        } catch (err) {
            toast.error(err.message || 'Failed to delete strategy');
        }
    };

    const handleToggle = async (strategy) => {
        try {
            await toggleStrategy(strategy.id);
            toast.success(strategy.is_active ? 'Strategy paused' : 'Strategy activated');
            fetchStrategies(page, limit, listFilters);
        } catch (err) {
            toast.error(err.message || 'Failed to toggle strategy');
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className={styles.page}>
            {/* Stats Bar */}
            <StrategyStats
                totalStrategies={stats.total}
                activeStrategies={stats.active}
                signalsToday={stats.signalsToday}
                onSignalsClick={() => navigate('/signals')}
                loading={loading && !initialLoaded}
            />

            {/* View Toggle — admin only */}
            {isAdmin && (
                <div className={styles.tabBar}>
                    <button
                        type="button"
                        className={`${styles.tabBtn} ${!mine ? styles.tabBtnActive : ''}`}
                        onClick={() => { navigate('/strategies'); setPage(1); }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>lists</span>
                        All Strategies
                    </button>
                    <button
                        type="button"
                        className={`${styles.tabBtn} ${mine ? styles.tabBtnActive : ''}`}
                        onClick={() => { navigate('/strategies?mine=true'); setPage(1); }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>person</span>
                        My Strategies
                    </button>
                </div>
            )}

            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>{isAdmin ? (mine ? 'My Strategies' : 'All Strategies') : 'Strategies'}</h1>
                    <p className={styles.subtitle}>
                        {isAdmin && !mine
                            ? 'Strategies created by all users'
                            : 'Manage your trading strategies and conditions'}
                    </p>
                </div>
                {canCreate && (
                    <Button variant="primary" onClick={() => setShowCreateModal(true)} icon="+">
                        New Strategy
                    </Button>
                )}
            </div>

            {/* Error Banner */}
            {error && (
                <div className={styles.errorBanner}>
                    <span>⚠️ {error.message}</span>
                    <button onClick={clearError}>×</button>
                </div>
            )}

            {/* Strategy Grid */}
            {loading && strategies.length === 0 ? (
                <div className={styles.spinnerContainer}><Spinner /></div>
            ) : strategies.length === 0 ? (
                <div className={styles.emptyState}>
                    <span className="material-symbols-outlined" style={{fontSize: 48, color: 'var(--text-muted)'}}>neurology</span>
                    <h3>No strategies yet</h3>
                    <p>Create your first strategy to start automated condition scanning.</p>
                    {canCreate && (
                        <Button variant="primary" onClick={() => setShowCreateModal(true)} icon="+">
                            Create Strategy
                        </Button>
                    )}
                </div>
            ) : (
                <div className={styles.grid}>
                    {strategies.map(strategy => (
                        <StrategyCard
                            key={strategy.id}
                            strategy={strategy}
                            onToggle={canEdit ? () => handleToggle(strategy) : null}
                            onEdit={canEdit ? () => handleEdit(strategy) : null}
                            onDelete={canDelete ? () => handleDeleteClick(strategy) : null}
                            onClick={() => navigate(`/strategies/${strategy.id}`)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className={styles.tableFooter}>
                    <span>Showing {strategies.length} of {total} strategies</span>
                    <div className={styles.pagination}>
                        <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>{'<'}</button>
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                            <button key={p} className={`${styles.pageBtn} ${page === p ? styles.active : ''}`} onClick={() => setPage(p)}>{p}</button>
                        ))}
                        <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{'>'}</button>
                    </div>
                    <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
                </div>
            )}

            {/* Create Modal */}
            <FullscreenModal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setFormData({}); }}
                title="Create New Strategy" subtitle="Set up conditions and actions for automated signal generation"
                size="fullscreen" showProgress={true} progress={formProgress} loading={loading}
                contentHeight="80vh" disableBackdropClick={true}
            >
                <StrategyForm onSubmit={handleCreate} onCancel={() => { setShowCreateModal(false); setFormData({}); }}
                    onFormDataChange={setFormData} loading={loading}
                />
            </FullscreenModal>

            {/* Edit Modal */}
            <FullscreenModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setStrategyToEdit(null); setFormData({}); }}
                title="Edit Strategy" subtitle={strategyToEdit ? `Editing: ${strategyToEdit.name}` : ''}
                size="fullscreen" showProgress={true} progress={formProgress} loading={loading}
                contentHeight="80vh" disableBackdropClick={true}
            >
                <StrategyForm strategy={strategyToEdit} onSubmit={handleUpdate}
                    onCancel={() => { setShowEditModal(false); setStrategyToEdit(null); setFormData({}); }}
                    onFormDataChange={setFormData} loading={loading}
                />
            </FullscreenModal>

            {/* Delete Confirmation */}
            <Modal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setStrategyToDelete(null); }}
                title="Confirm Deletion" size="small"
                actions={<>
                    <Button variant="outline" onClick={() => { setShowDeleteModal(false); setStrategyToDelete(null); }} disabled={loading}>Cancel</Button>
                    <Button variant="danger" onClick={confirmDelete} loading={loading}>Delete Strategy</Button>
                </>}
            >
                <div className={styles.deleteConfirm}>
                    <p>Are you sure you want to delete <strong>{strategyToDelete?.name || 'this strategy'}</strong>?</p>
                    <p className={styles.deleteWarning}>This action cannot be undone. All signals generated by this strategy will be preserved.</p>
                </div>
            </Modal>
        </div>
    );
};

export default Strategies;
